import React from "react";

import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Grid,
  Divider,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  Chip,
} from "@mui/material";

const COLORS = {
  black: "#111111",
  orange: "#ff8c00",
  camel: "#c19a6b",
  gray: "#6b7280",
  lightGray: "#f3f4f6",
  white: "#ffffff",
  darkOrange: "#d97706",
};

const UserDetailsPage = ({ selectedUser }) => {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!selectedUser) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor={COLORS.lightGray}
      >
        <Typography
          variant="h6"
          color={COLORS.gray}
          fontWeight={600}
        >
          No user selected.
        </Typography>
      </Box>
    );
  }


  const {
    full_name,
    user_type,
    profile_image,
    image,
    teacher_data,
    payroll_data,
    student_data,
    payments,
    is_supportstaff,
    department,
    dependent_student,
    salary,
    contact,
    date_add,
  } = selectedUser;


  const getImageUrl = (img) => {
    if (!img) return "";

    if (img.startsWith("http")) {
      return img;
    }

    return `http://127.0.0.1:8000${img}`;
  };


  const SectionTitle = ({ children }) => (
    <Box mb={2}>
      <Typography
        variant="h6"
        fontWeight={800}
        color={COLORS.black}
        sx={{
          borderLeft:`5px solid ${COLORS.orange}`,
          pl:1.5,
          letterSpacing:0.5
        }}
      >
        {children}
      </Typography>

      <Divider
        sx={{
          mt:1,
          borderColor:COLORS.camel
        }}
      />

    </Box>
  );


  const InfoItem = ({label,value}) => (
    <Box>
      <Typography
        variant="caption"
        fontWeight={700}
        color={COLORS.orange}
      >
        {label}
      </Typography>

      <Typography
        variant="body1"
        fontWeight={600}
        color={COLORS.black}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  );



  return (

    <Box
      minHeight="100vh"
      p={{
        xs:2,
        md:4
      }}
      bgcolor="#eeeeee"
    >


      {/* PROFILE HEADER */}

      <Card
        sx={{
          borderRadius:4,
          mb:3,
          overflow:"hidden",
          boxShadow:"0 10px 30px rgba(0,0,0,0.15)"
        }}
      >

<Box
  sx={{
    background:
    `linear-gradient(135deg,
    ${COLORS.black},
    ${COLORS.camel})`,
    p:3,
    color:"#ffffff"
  }}
>


          <Grid
            container
            spacing={3}
            alignItems="center"
            direction={isMobile ? "column":"row"}
          >

            <Grid item>

              <Avatar
                src={getImageUrl(profile_image || image)}
                sx={{
                  width:110,
                  height:110,
                  border:
                  `4px solid ${COLORS.orange}`,
                  boxShadow:
                  "0 5px 20px rgba(0,0,0,.4)"
                }}
              />

            </Grid>


            <Grid item xs>

              <Typography
                variant={isMobile?"h5":"h4"}
                fontWeight={900}
                color="white"
              >
                {full_name || "N/A"}
              </Typography>


              <Chip
                label={
                  is_supportstaff
                  ?
                  "Support Staff"
                  :
                  user_type || "User"
                }
                sx={{
                  mt:1,
                  bgcolor:COLORS.orange,
                  color:"white",
                  fontWeight:800
                }}
              />


              {
                user_type==="student" &&
                student_data?.parent_contact &&

                <Typography
                  mt={2}
                  fontWeight={700}
                  color="#fff"
                >
                  Parent Contact:
                  {" "}
                  {student_data.parent_contact}
                </Typography>
              }


            </Grid>


          </Grid>

        </Box>

      </Card>





      {/* TEACHER SECTION */}


      {
        user_type==="teacher" &&
        teacher_data &&

        <Card
          sx={{
            mb:3,
            borderRadius:3,
            boxShadow:
            "0 8px 20px rgba(0,0,0,.12)"
          }}
        >

          <CardContent>


            <SectionTitle>
              Teacher Information 
            </SectionTitle>


            <Grid container spacing={3}>

              <Grid item xs={12} md={6}>

                <InfoItem
                  label="Division"
                  value={teacher_data.division_name}
                />


                <InfoItem
                  label="Qualification"
                  value={teacher_data.qualification}
                />


                <InfoItem
                  label="Contact"
                  value={teacher_data.contact}
                />

              </Grid>



              <Grid item xs={12} md={6}>

                <InfoItem
                  label="Dependent Student"
                  value={teacher_data.dependent_student}
                />


                <InfoItem
                  label="Salary"
                  value={
                    teacher_data.salary
                    ?
                    `$${teacher_data.salary}`
                    :
                    "N/A"
                  }
                />


                <InfoItem
                  label="Date Added"
                  value={teacher_data.date_add}
                />


              </Grid>

            </Grid>


          </CardContent>


        </Card>
      }
      {/* PAYROLL INFORMATION */}

      {
        user_type === "teacher" &&
        payroll_data &&

        <Card
          sx={{
            mb:3,
            borderRadius:3,
            boxShadow:"0 8px 20px rgba(0,0,0,.12)"
          }}
        >

          <CardContent>

            <SectionTitle>
              Payroll Information
            </SectionTitle>


            <Grid container spacing={3}>

              <Grid item xs={12} md={6}>

                <InfoItem
                  label="Month"
                  value={payroll_data.month}
                />

                <InfoItem
                  label="Year"
                  value={payroll_data.year}
                />

                <InfoItem
                  label="Basic Salary"
                  value={
                    payroll_data.basic_salary
                    ?
                    `$${payroll_data.basic_salary}`
                    :
                    "N/A"
                  }
                />

              </Grid>


              <Grid item xs={12} md={6}>


                <InfoItem
                  label="Bonus"
                  value={
                    payroll_data.bonus
                    ?
                    `$${payroll_data.bonus}`
                    :
                    "N/A"
                  }
                />


                <InfoItem
                  label="Deductions"
                  value={
                    payroll_data.deductions
                    ?
                    `$${payroll_data.deductions}`
                    :
                    "N/A"
                  }
                />


                <InfoItem
                  label="Total Paid"
                  value={
                    payroll_data.total_paid
                    ?
                    `$${payroll_data.total_paid}`
                    :
                    "N/A"
                  }
                />


              </Grid>

            </Grid>


          </CardContent>

        </Card>

      }




      {/* STUDENT INFORMATION */}


      {
        user_type==="student" &&
        student_data &&

        <Card
          sx={{
            mb:3,
            borderRadius:3,
            boxShadow:
            "0 8px 20px rgba(0,0,0,.12)"
          }}
        >

          <CardContent>


            <SectionTitle>
              Student Information
            </SectionTitle>


            <Grid container spacing={3}>


              <Grid item xs={12} md={6}>


                <InfoItem
                  label="Grade Class"
                  value={student_data.grade_class_name}
                />


                <InfoItem
                  label="Division"
                  value={student_data.division_assigned_name}
                />


                <InfoItem
                  label="Gender"
                  value={student_data.gender}
                />


                <InfoItem
                  label="Date Of Birth"
                  value={student_data.date_of_birth}
                />


                <InfoItem
                  label="Health Status"
                  value={student_data.health_status}
                />

              </Grid>



              <Grid item xs={12} md={6}>


                <InfoItem
                  label="Previous School"
                  value={student_data.previous_school}
                />


                <InfoItem
                  label="Previous Class"
                  value={student_data.previous_class}
                />


                <InfoItem
                  label="Major Subject"
                  value={student_data.major_subject}
                />


                <InfoItem
                  label="Mother Name"
                  value={student_data.mother_name}
                />


                <InfoItem
                  label="Father Name"
                  value={student_data.father_name}
                />


              </Grid>


            </Grid>


          </CardContent>

        </Card>

      }






      {/* PAYMENT HISTORY */}



      {
        user_type==="student" &&

        <Card
          sx={{
            mb:3,
            borderRadius:3,
            boxShadow:
            "0 8px 20px rgba(0,0,0,.12)"
          }}
        >

          <CardContent>


            <SectionTitle>
              Payment History
            </SectionTitle>



            {
              payments &&
              payments.length > 0 ?

              <Paper
                sx={{
                  overflowX:"auto",
                  borderRadius:2
                }}
              >

                <Table
                  size={
                    isMobile
                    ?
                    "small"
                    :
                    "medium"
                  }
                >


                  <TableHead>


                    <TableRow
                      sx={{
                        bgcolor:COLORS.black
                      }}
                    >

                      {
                        [
                          "Semester",
                          "Class",
                          "Total Fee",
                          "Paid",
                          "Balance",
                          "Date"
                        ].map(
                          (head)=>(
                            <TableCell
                              key={head}
                              sx={{
                                color:"#fff",
                                fontWeight:800
                              }}
                            >
                              {head}
                            </TableCell>
                          )
                        )
                      }


                    </TableRow>


                  </TableHead>



                  <TableBody>


                    {
                      payments.map(
                        (payment,index)=>(

                        <TableRow
                          key={index}
                          hover
                          sx={{
                            "&:hover":{
                              bgcolor:"#fff3e0"
                            }
                          }}
                        >

                          <TableCell>
                            {payment.semester || "N/A"}
                          </TableCell>


                          <TableCell>
                            {payment.student_class || "N/A"}
                          </TableCell>


                          <TableCell>
                            ${payment.total_fee || "0.00"}
                          </TableCell>


                          <TableCell>
                            ${payment.amount_paid || "0.00"}
                          </TableCell>


                          <TableCell
                            sx={{
                              color:COLORS.orange,
                              fontWeight:700
                            }}
                          >
                            ${payment.balance_fee || "0.00"}
                          </TableCell>


                          <TableCell>
                            {payment.date || "N/A"}
                          </TableCell>


                        </TableRow>

                      ))
                    }


                  </TableBody>


                </Table>


              </Paper>


              :

              <Box
                p={3}
                textAlign="center"
                bgcolor={COLORS.lightGray}
                borderRadius={2}
              >

                <Typography
                  color={COLORS.gray}
                  fontWeight={600}
                >
                  No payment records available.
                </Typography>

              </Box>


            }


          </CardContent>

        </Card>

      }








      {/* SUPPORT STAFF */}



      {
        is_supportstaff &&


        <Card
          sx={{
            borderRadius:4,
            boxShadow:
            "0 10px 30px rgba(0,0,0,.15)"
          }}
        >


          <CardContent>


            <SectionTitle>
              Support Staff Details
            </SectionTitle>



            <Grid
              container
              spacing={4}
              alignItems="center"
            >


              <Grid item>


                <Avatar

                  src={
                    getImageUrl(
                      image
                    )
                  }

                  sx={{

                    width:
                    isMobile
                    ?
                    120
                    :
                    170,


                    height:
                    isMobile
                    ?
                    120
                    :
                    170,


                    border:
                    `5px solid ${COLORS.orange}`

                  }}

                />


              </Grid>





              <Grid item xs>


                <Paper

                  sx={{

                    p:3,

                    bgcolor:"#fafafa",

                    borderRadius:3

                  }}

                >


                  <Grid container spacing={3}>


                    <Grid item xs={12} md={6}>

                      <InfoItem
                        label="Full Name"
                        value={full_name}
                      />


                      <InfoItem
                        label="Department"
                        value={department}
                      />


                      <InfoItem
                        label="Dependent Student"
                        value={dependent_student}
                      />

                    </Grid>



                    <Grid item xs={12} md={6}>


                      <InfoItem
                        label="Salary"
                        value={
                          salary
                          ?
                          `$${salary}`
                          :
                          "N/A"
                        }
                      />


                      <InfoItem
                        label="Contact"
                        value={contact}
                      />


                      <InfoItem
                        label="Date Added"
                        value={date_add}
                      />


                    </Grid>



                  </Grid>


                </Paper>


              </Grid>


            </Grid>






            {/* SUPPORT STAFF PAYROLL TABLE */}


            <Box mt={5}>


              <SectionTitle>
                Payroll History
              </SectionTitle>



              {
                selectedUser.payrolls &&
                selectedUser.payrolls.length > 0 ?


                <Paper
                  sx={{
                    overflowX:"auto",
                    borderRadius:3
                  }}
                >


                  <Table>


                    <TableHead>


                      <TableRow
                        sx={{
                          bgcolor:COLORS.black
                        }}
                      >


                        {
                          [
                            "Month-Year",
                            "Basic Salary",
                            "Bonus",
                            "Deduction",
                            "Advance",
                            "Total Paid",
                            "Date"
                          ].map(
                            item=>(

                            <TableCell
                              key={item}
                              sx={{
                                color:"#fff",
                                fontWeight:800
                              }}
                            >
                              {item}
                            </TableCell>

                          ))
                        }


                      </TableRow>


                    </TableHead>



                    <TableBody>


                      {
                        selectedUser.payrolls.map(
                          (pay,i)=>(


                          <TableRow
                            hover
                            key={i}
                          >


                            <TableCell>
                              {pay.month} - {pay.year}
                            </TableCell>


                            <TableCell>
                              ${Number(pay.basic_salary).toFixed(2)}
                            </TableCell>


                            <TableCell>
                              ${Number(pay.bonus).toFixed(2)}
                            </TableCell>


                            <TableCell>
                              ${Number(pay.deductions).toFixed(2)}
                            </TableCell>


                            <TableCell>
                              ${Number(pay.salary_advance).toFixed(2)}
                            </TableCell>


                            <TableCell
                              sx={{
                                color:COLORS.orange,
                                fontWeight:800
                              }}
                            >
                              ${Number(pay.total_paid).toFixed(2)}
                            </TableCell>


                            <TableCell>
                              {
                                new Date(
                                  pay.created_at
                                ).toLocaleDateString()
                              }
                            </TableCell>


                          </TableRow>


                        ))
                      }



                    </TableBody>



                  </Table>


                </Paper>



                :


                <Box
                  p={3}
                  bgcolor={COLORS.lightGray}
                  textAlign="center"
                  borderRadius={2}
                >

                  <Typography
                    color={COLORS.gray}
                    fontWeight={600}
                  >
                    No payroll records available.
                  </Typography>

                </Box>


              }


            </Box>




          </CardContent>


        </Card>


      }



    </Box>

  );

};


export default UserDetailsPage;